import { Fragment } from 'react';
import type { VoidFunctionComponent } from 'react';

import List from '../../generic/List';
import Heading from '../../generic/Text/Heading';

import { ActivityFeedRow } from './ActivityFeedRow';
import styles from './ActivityFeed.module.less';
import type { LegalAgentActivityEntry } from '../../../../shared/types/routes/legalAgent';

type Props = {
  isLoading: boolean;
  isError: boolean;
  entries: LegalAgentActivityEntry[] | null;
  //  A filter that matched nothing is not an organization with no activity.
  isFiltered: boolean;
  //  Name each row's project, for any scope other than exactly one project.
  showScope: boolean;
};

export const ACTIVITY_ERROR_COPY = 'Activity couldn\'t be loaded. Try again in a moment.';
export const ACTIVITY_EMPTY_COPY = 'No Legal Agent activity yet.';
export const ACTIVITY_FILTERED_EMPTY_COPY = 'No activity matches this selection.';

const LOADING_ROW_KEYS = ['1', '2', '3', '4', '5', '6'];

//  The feed sorts concluded entries by the hour they group and shows when the last one happened, so near a month
//  boundary a month can reappear after the next one; it joins its first group rather than repeating the heading.
export function groupByMonth(entries: LegalAgentActivityEntry[]): Array<{ month: string; entries: LegalAgentActivityEntry[] }> {
  const groups = new Map<string, LegalAgentActivityEntry[]>();
  entries.forEach((entry) => {
    const month = new Date(entry.occurredAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const monthEntries = groups.get(month) ?? [];
    monthEntries.push(entry);
    groups.set(month, monthEntries);
  });
  return [...groups].map(([month, monthEntries]) => ({ month, entries: monthEntries }));
}

export const ActivityFeed: VoidFunctionComponent<Props> = ({ isLoading, isError, entries, isFiltered, showScope }) => {
  const rows = entries ?? [];

  return (
    //  Skeleton rows are wordless SVGs, so aria-busy tells a screen reader the feed is loading.
    <div aria-busy={isLoading} data-test-id="legal-agent-activity-feed">
      <List>
        <List.Body>
          {isLoading && LOADING_ROW_KEYS.map((key) => <List.RowLoading key={key} isInteractive={false} />)}
          {!isLoading && (isError || rows.length === 0) && (
            <div className={styles.empty} role={isError ? 'alert' : 'status'}>
              {isError ? ACTIVITY_ERROR_COPY : (isFiltered ? ACTIVITY_FILTERED_EMPTY_COPY : ACTIVITY_EMPTY_COPY)}
            </div>
          )}
          {!isLoading && groupByMonth(rows).map((group) => (
            <Fragment key={group.month}>
              <List.Header hasEvenPadding>
                <div className={styles.month}><Heading level={2}>{group.month}</Heading></div>
              </List.Header>
              {group.entries.map((entry) => <ActivityFeedRow key={entry.id} entry={entry} showScope={showScope} />)}
            </Fragment>
          ))}
        </List.Body>
      </List>
    </div>
  );
};
